import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getCompanies, createCompany, updateCompany, deleteCompany } from '../../lib/api';
import ImageUpload from '../../components/common/ImageUpload';

const AdminCompanies = () => {
  const { t } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name_ar: '', name_en: '', slug: '', logo_url: '',
    description_ar: '', description_en: '',
    services_ar: [], services_en: [],
    contact_email: '', contact_phone: '',
    address_ar: '', address_en: '',
    color_accent: '#B76E79', order: 0, is_active: true
  });

  const loadCompanies = async () => {
    try {
      const res = await getCompanies();
      setCompanies(res.data);
    } catch (error) {
      toast.error(t('خطأ في تحميل البيانات', 'Error loading data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCompanies(); }, []);

  const openCreateDialog = () => {
    setSelectedCompany(null);
    setFormData({
      name_ar: '', name_en: '', slug: '', logo_url: '',
      description_ar: '', description_en: '',
      services_ar: [], services_en: [],
      contact_email: '', contact_phone: '',
      address_ar: '', address_en: '',
      color_accent: '#B76E79', order: companies.length, is_active: true
    });
    setDialogOpen(true);
  };

  const openEditDialog = (company) => {
    setSelectedCompany(company);
    setFormData({
      name_ar: company.name_ar, name_en: company.name_en,
      slug: company.slug, logo_url: company.logo_url || '',
      description_ar: company.description_ar, description_en: company.description_en,
      services_ar: company.services_ar || [], services_en: company.services_en || [],
      contact_email: company.contact_email || '', contact_phone: company.contact_phone || '',
      address_ar: company.address_ar || '', address_en: company.address_en || '',
      color_accent: company.color_accent, order: company.order, is_active: company.is_active
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name_ar || !formData.name_en || !formData.slug) {
      toast.error(t('يرجى ملء جميع الحقول المطلوبة', 'Please fill all required fields'));
      return;
    }

    setSubmitting(true);
    try {
      if (selectedCompany) {
        await updateCompany(selectedCompany.id, formData);
        toast.success(t('تم تحديث الشركة بنجاح', 'Company updated successfully'));
      } else {
        await createCompany(formData);
        toast.success(t('تم إنشاء الشركة بنجاح', 'Company created successfully'));
      }
      setDialogOpen(false);
      loadCompanies();
    } catch (error) {
      toast.error(t('حدث خطأ', 'An error occurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCompany(selectedCompany.id);
      toast.success(t('تم حذف الشركة بنجاح', 'Company deleted successfully'));
      setDeleteDialogOpen(false);
      loadCompanies();
    } catch (error) {
      toast.error(t('حدث خطأ أثناء الحذف', 'Error deleting company'));
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-raad-rose border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-companies">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('الشركات', 'Companies')}</h1>
          <p className="text-gray-400">{t('إدارة شركات المجموعة', 'Manage group companies')}</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-gradient-raad" data-testid="add-company-btn">
          <Plus className="w-4 h-4 me-2" />
          {t('إضافة شركة', 'Add Company')}
        </Button>
      </div>

      {companies.length === 0 ? (
        <Card className="bg-raad-card border-white/10">
          <CardContent className="py-12 text-center">
            <p className="text-gray-400">{t('لا توجد شركات', 'No companies yet')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {companies.map((company) => (
            <Card key={company.id} className="bg-raad-card border-white/10">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  {company.logo_url ? (
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-white/5">
                      <img 
                        src={company.logo_url} 
                        alt={t(company.name_ar, company.name_en)}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: company.color_accent }}
                    >
                      <span className="text-white font-bold text-xl">
                        {t(company.name_ar, company.name_en)[0]}
                      </span>
                    </div>
                  )}
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(company)} className="text-gray-400 hover:text-white">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => { setSelectedCompany(company); setDeleteDialogOpen(true); }} className="text-gray-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <h3 className="text-white font-semibold mb-1">{t(company.name_ar, company.name_en)}</h3>
                <p className="text-gray-400 text-sm line-clamp-2">{t(company.description_ar, company.description_en)}</p>
                <div className="mt-4 flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs ${company.is_active ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-500'}`}>
                    {company.is_active ? t('نشط', 'Active') : t('غير نشط', 'Inactive')}
                  </span>
                  <span className="text-gray-500 text-xs">/{company.slug}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-raad-card border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {selectedCompany ? t('تعديل الشركة', 'Edit Company') : t('إضافة شركة', 'Add Company')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">{t('اسم الشركة (عربي)', 'Company Name (Arabic)')} *</Label>
                <Input value={formData.name_ar} onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="rtl" />
              </div>
              <div>
                <Label className="text-gray-300">{t('اسم الشركة (إنجليزي)', 'Company Name (English)')} *</Label>
                <Input value={formData.name_en} onChange={(e) => { setFormData({ ...formData, name_en: e.target.value, slug: generateSlug(e.target.value) }); }} className="bg-white/5 border-white/10 text-white mt-1" dir="ltr" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">{t('الرابط', 'Slug')} *</Label>
                <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="ltr" />
              </div>
              <div>
                <Label className="text-gray-300">{t('اللون', 'Color')}</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="color" value={formData.color_accent} onChange={(e) => setFormData({ ...formData, color_accent: e.target.value })} className="w-14 h-10 p-1 bg-white/5 border-white/10" />
                  <Input value={formData.color_accent} onChange={(e) => setFormData({ ...formData, color_accent: e.target.value })} className="bg-white/5 border-white/10 text-white flex-1" />
                </div>
              </div>
            </div>
            
            {/* Logo Upload */}
            <div>
              <ImageUpload
                label={t('شعار الشركة', 'Company Logo')}
                value={formData.logo_url}
                onChange={(url) => setFormData({ ...formData, logo_url: url })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">{t('الوصف (عربي)', 'Description (Arabic)')}</Label>
                <Textarea value={formData.description_ar} onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="rtl" rows={3} />
              </div>
              <div>
                <Label className="text-gray-300">{t('الوصف (إنجليزي)', 'Description (English)')}</Label>
                <Textarea value={formData.description_en} onChange={(e) => setFormData({ ...formData, description_en: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="ltr" rows={3} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">{t('الخدمات (عربي)', 'Services (Arabic)')}</Label>
                <Textarea value={formData.services_ar.join('\n')} onChange={(e) => setFormData({ ...formData, services_ar: e.target.value.split('\n').filter(s => s.trim()) })} className="bg-white/5 border-white/10 text-white mt-1" dir="rtl" rows={3} placeholder={t('خدمة واحدة في كل سطر', 'One service per line')} />
              </div>
              <div>
                <Label className="text-gray-300">{t('الخدمات (إنجليزي)', 'Services (English)')}</Label>
                <Textarea value={formData.services_en.join('\n')} onChange={(e) => setFormData({ ...formData, services_en: e.target.value.split('\n').filter(s => s.trim()) })} className="bg-white/5 border-white/10 text-white mt-1" dir="ltr" rows={3} placeholder="One service per line" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">{t('البريد الإلكتروني', 'Email')}</Label>
                <Input value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" />
              </div>
              <div>
                <Label className="text-gray-300">{t('رقم الهاتف', 'Phone')}</Label>
                <Input value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1 phone-ltr" dir="ltr" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
                <Label className="text-gray-300">{t('نشط', 'Active')}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-gray-300">{t('الترتيب', 'Order')}</Label>
                <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })} className="bg-white/5 border-white/10 text-white w-20" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>{t('إلغاء', 'Cancel')}</Button>
              <Button type="submit" disabled={submitting} className="bg-gradient-raad">
                {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : selectedCompany ? t('تحديث', 'Update') : t('إنشاء', 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-raad-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t('تأكيد الحذف', 'Confirm Delete')}</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">{t('هل أنت متأكد من حذف هذه الشركة؟', 'Are you sure you want to delete this company?')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">{t('إلغاء', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">{t('حذف', 'Delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCompanies;
